<template>
  <div>
    <v-form ref="form" v-model="formValid" @submit.prevent>
      <div class="subtitle1 mb-1">
        {{ $t("modals.addZtnaRule.chooseLabelType") }}
      </div>
      <div class="body2 text-indigo-medium mb-4">
        {{ $t("modals.addZtnaRule.chooseLabelTypeSubtitle") }}
      </div>
      <v-radio-group v-model="localValue.item.labelType" inline>
        <v-radio
          :label="$t('modals.addZtnaRule.devices')"
          :value="ZtnaLabelType.DEVICE"
          data-testid="ztna-modal-device-label-radio"
        ></v-radio>
        <v-radio
          class="ml-3"
          :label="$t('modals.addZtnaRule.users')"
          data-testid="ztna-modal-user-label-radio"
          :value="ZtnaLabelType.USER"
        ></v-radio>
      </v-radio-group>
      <v-autocomplete
        v-if="localValue.item.labelType === ZtnaLabelType.DEVICE"
        v-model="localValue.item.labels"
        :items="labels"
        :label="$t('modals.addZtnaRule.labelsAutocompleteLabel')"
        :placeholder="$t('modals.addZtnaRule.placeholders.labels')"
        :hint="$t('modals.addZtnaRule.labelsHint')"
        persistent-hint
        variant="outlined"
        :rules="[labelRules]"
        multiple
        return-object
        chips
        closable-chips
        clear-on-select
        data-testid="device-settings-tamper-resistance-labels"
        item-value="id"
        item-title="name"
      >
        <template v-slot:chip="{ props, item, index }">
          <v-chip
            :closable="true"
            :color="getLabelsChipClass(item.raw.color, index)"
            :text="item.raw.name"
            class="label-chip"
            close-icon="$closeCircle"
            label
            rounded="3"
            size="default"
            v-bind="props"
            variant="flat"
            @click:close="onLabelClick(index)"
          >
            <div class="d-flex align-center">
              <span
                class="ml-1 mr-2 coro-label text-primary text-uppercase"
                :class="{ 'text-red-dark': isLabelInErrorState(index) }"
                v-text="item.title"
              />
            </div>
            <template v-slot:close>
              <v-icon
                icon="icon-close-xs"
                :color="isLabelInErrorState(index) ? 'red-dark' : 'indigo-medium'"
              ></v-icon>
            </template>
          </v-chip>
        </template>
        <template v-slot:item="{ item, props }">
          <v-list-item v-bind="props" title="">
            <template #default>
              <div class="d-flex align-center label-popup-block">
                <div class="color-preview mr-2" :style="{ 'background-color': item.raw.color }" />
                <span v-text="item.raw.name" />
              </div>
            </template>
          </v-list-item>
        </template>
      </v-autocomplete>
      <template v-else>
        <template v-if="!isUserBasedAuthEnabled">
          <div
            v-if="localValue.item.userLabels && localValue.item.userLabels.length > 0"
            class="error-block d-flex align-top mb-5"
          >
            <v-icon class="mr-3 ml-4" icon="$warning"></v-icon>
            <div>
              <span
                class="body2 text-red-dark"
                v-html="$t('modals.addZtnaRule.userBasedZtnaIsRequired')"
              />
              <span class="body2 text-red-dark">
                {{ $t("modals.addZtnaRule.enableIn") }}
              </span>
              <span class="coro-link body2 cursor-pointer ml-1 mr-1" @click="goToDeviceSettings()">
                {{ $t("modals.addZtnaRule.deviceSettings") }}
              </span>
              <span class="body2 text-red-dark">
                {{ $t("modals.addZtnaRule.andCreate") }}
              </span>
            </div>
          </div>
          <div v-else class="warning-block d-flex align-top mb-5">
            <v-icon class="mr-3 ml-4" icon="$warning"></v-icon>
            <div>
              <span class="body2" v-html="$t('modals.addZtnaRule.userBasedZtnaIsRequired')" />
              <span class="body2">
                {{ $t("modals.addZtnaRule.enableIn") }}
              </span>
              <span class="coro-link cursor-pointer ml-1 mr-1" @click="goToDeviceSettings()">
                {{ $t("modals.addZtnaRule.deviceSettings") }}
              </span>
              <span class="body2">
                {{ $t("modals.addZtnaRule.andCreate") }}
              </span>
            </div>
          </div>
        </template>
        <div
          v-if="!userLabelsLoading && !userLabels.length"
          class="info-block px-3 py-1 mb-4 d-flex align-center"
          data-testid="add-ztna-modal-no-user-labels-banner"
        >
          <v-icon size="24" icon="$info" />
          <div class="body2 ml-2">
            <span class="mr-1">{{ $t("modals.addZtnaRule.emptyLabelsDisclaimer.part1") }}</span>
            <span class="mr-1 coro-link cursor-pointer" @click="goToUsersLabels()">
              {{ $t("modals.addZtnaRule.emptyLabelsDisclaimer.part2") }}
            </span>
            <span class="mr-1">{{ $t("modals.addZtnaRule.emptyLabelsDisclaimer.part3") }}</span>
          </div>
        </div>
        <v-autocomplete
          v-model="localValue.item.userLabels"
          :items="userLabels"
          :label="$t('modals.addZtnaRule.userLabelsAutocompleteLabel')"
          :placeholder="$t('modals.addZtnaRule.placeholders.labels')"
          :hint="$t('modals.addZtnaRule.labelsHint')"
          persistent-hint
          variant="outlined"
          :rules="[userLabelRules]"
          multiple
          return-object
          chips
          closable-chips
          clear-on-select
          data-testid="device-settings-user-labels"
          item-value="id"
          item-title="name"
        >
          <template v-slot:chip="{ props, item, index }">
            <v-chip
              v-bind="props"
              :closable="true"
              variant="flat"
              size="default"
              close-icon="$closeCircle"
              :text="item.raw.name"
              class="label-chip"
              label
              rounded="3"
              :color="getUserLabelsChipClass(item.raw.color)"
              @click:close="onLabelClick(index)"
            >
              <div class="d-flex align-center">
                <span
                  class="ml-1 mr-2 coro-label text-primary text-uppercase"
                  :class="{ 'text-red-dark': !isUserBasedAuthEnabled }"
                  v-text="item.title"
                />
              </div>
              <template v-slot:close>
                <v-icon
                  icon="icon-close-xs"
                  :color="!isUserBasedAuthEnabled ? 'red-dark' : 'indigo-medium'"
                ></v-icon>
              </template>
            </v-chip>
          </template>
          <template v-slot:item="{ item, props }">
            <v-list-item v-bind="props" title="">
              <template #default>
                <div class="d-flex align-center label-popup-block">
                  <div class="color-preview mr-2" :style="{ 'background-color': item.raw.color }" />
                  <span v-text="item.raw.name" />
                </div>
              </template>
            </v-list-item>
          </template>
        </v-autocomplete>
      </template>
      <div class="subtitle1 mb-4 mt-2">
        {{ $t("modals.addZtnaRule.titles.resourceDetails") }}
      </div>
      <v-text-field
        class="mb-2"
        v-model="localValue.item.name"
        variant="outlined"
        :counter="64"
        :rules="[resourceNameRule, required(), maxCharactersRule(64)]"
        :label="$t('modals.addZtnaRule.placeholders.resourceName')"
        :aria-label="$t('modals.addZtnaRule.placeholders.resourceName')"
      />
      <v-text-field
        class="mb-2"
        v-model="localValue.item.description"
        :counter="255"
        :rules="[maxCharactersRule(255)]"
        variant="outlined"
        :label="$t('modals.addZtnaRule.placeholders.resourceDescription')"
        :aria-label="$t('modals.addZtnaRule.placeholders.resourceDescription')"
      />
      <div class="mb-4">
        <div class="subtitle1">{{ $t("modals.addZtnaRule.titles.resourceConfigurations") }}</div>
        <div class="caption text-grey-darken-1">
          {{ $t("modals.addZtnaRule.upToInstances", { max: MAX_INSTANCES }) }}
        </div>
        <div class="info-block d-flex py-2 px-4 my-4 align-start">
          <v-icon size="24" class="mr-3" icon="$info" />
          <div class="body2">
            {{ $t("modals.addZtnaRule.bannerDescription") }}
          </div>
        </div>
      </div>

      <v-expansion-panels multiple flat v-model="openedPanels">
        <ztna-rule-config
          v-for="(instance, index) in localValue.item.configs"
          eager
          :key="index"
          :config="instance"
          :index="index + 1"
          :show-remove-btn="localValue.item.configs.length > 1"
          :errors="getErrorsByIndex(index)"
          :rules-count="localValue.item.configs.length"
          @validate="validate"
          @update:errors="updateErrors()"
          @update:localValue="onUpdateRuleConfig($event, index)"
          @update:valid="(valid) => onConfigValidityChange(index, valid)"
          @remove-instance="onRemoveInstance(index)"
        />
      </v-expansion-panels>
      <div
        v-if="localValue.item.configs.length < MAX_INSTANCES"
        class="coro-link body2 mb-4"
        role="button"
        @click="onAddInstance"
      >
        {{ "+ " + $t("modals.addZtnaRule.addResourceConfigField") }}
      </div>
    </v-form>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, type PropType, ref, watch } from "vue";
import type { VuetifyFormRef } from "@/types";
import { useI18n } from "vue-i18n";
import { type DialogDataConfig, useDialogsStore } from "@/_store/dialogs.module";
import { maxCharactersRule, required } from "@/_helpers/validators";
import { storeToRefs } from "pinia";
import { type DeviceLabel, useDevicesSettingsStore } from "@/_store/devices-settings.module";
import { type RuleConfig, ZtnaLabelType } from "@/_store/network/ztna.module";
import ZtnaRuleConfig from "@/components/network/ztna/AddZtnaModal/ZtnaRuleConfig.vue";
import { ConfigType } from "@/constants/network.ts";
import cloneDeep from "lodash/cloneDeep";
import type { ErrorPayload } from "@/_store/errors.module.ts";
import { type UserLabel, useUserLabelsStore } from "@/_store/users-settings/user-labels.module.ts";
import { RouteName } from "@/constants/routes.ts";
import { useRouter } from "vue-router";

const MAX_INSTANCES = 5;

const RULE_CONFIG: RuleConfig = {
  type: ConfigType.IP,
  value: "",
  ports: [],
  protocol: null,
  domains: {},
};

const defaultRulesConfigs = {
  configs: [
    {
      ...RULE_CONFIG,
    },
  ],
  description: "",
  name: "",
  labelType: ZtnaLabelType.DEVICE,
};

export default defineComponent({
  components: { ZtnaRuleConfig },
  props: {
    config: {
      type: Object as PropType<DialogDataConfig>,
      required: true,
    },
    errors: {
      type: Array as PropType<ErrorPayload[]>,
      default() {
        return [];
      },
    },
  },
  emits: ["update:valid", "update:localValue", "update:errors"],
  setup(props, { emit }) {
    const form = ref<VuetifyFormRef>();
    const { t } = useI18n();
    const formValid = ref(true);
    const previousName = ref("");
    const nameErrorMessage = ref("");
    const labelsErrorMessage = ref("");
    const configsValidity = ref<Record<number, boolean>>({});
    const localValue = ref(
      props.config.item
        ? { item: { ...props.config.item } }
        : { item: { ...cloneDeep(defaultRulesConfigs) } }
    );
    const openedPanels = ref<number[]>(props.config.item ? [] : [0]);
    const deviceSettingsStore = useDevicesSettingsStore();
    const userLabelsStore = useUserLabelsStore();
    const router = useRouter();
    const { getLabels } = deviceSettingsStore;
    const { labels } = storeToRefs(deviceSettingsStore);
    const { labels: userLabels, loading: userLabelsLoading } = storeToRefs(userLabelsStore);

    const isUserBasedAuthEnabled = computed(() => {
      return deviceSettingsStore.settings.enableUserBasedAuthentication;
    });

    const labelRules = (labels: DeviceLabel[]) => {
      if (!labels?.length) return t("validations.required");

      if (labelsError.value) {
        labelsErrorMessage.value = labelsError.value?.message || labelsErrorMessage.value;
        return labelsErrorMessage.value;
      }

      return true;
    };

    const userLabelRules = (labels: UserLabel[]) => {
      if (!labels?.length) return t("validations.required");

      if (labelsError.value) {
        labelsErrorMessage.value = labelsError.value?.message || labelsErrorMessage.value;
        return labelsErrorMessage.value;
      }

      if (!isUserBasedAuthEnabled.value) {
        return t("modals.addZtnaRule.userBasedZtnaInvalid");
      }

      return true;
    };

    const goToDeviceSettings = () => {
      useDialogsStore().closeDialog();
      router.push({ name: RouteName.DEVICES_SETTINGS_DEVICE_SETTINGS_TAB });
    };

    const goToUsersLabels = () => {
      useDialogsStore().closeDialog();
      router.push({ name: RouteName.USERS_SETTINGS_LABELS_TAB });
    };

    const nameError = computed(() => {
      return props.errors.find((e: ErrorPayload) => e.field === "name");
    });

    const labelsError = computed(() => {
      return props.errors.find((e: ErrorPayload) => e.field === "labels");
    });

    const rootError = computed(() => {
      return props.errors.find((e: ErrorPayload) => !e.hasOwnProperty("field"));
    });

    const resourceNameRule = (name: string = "") => {
      if (nameError.value || previousName.value === name) {
        previousName.value = name;
        nameErrorMessage.value = nameError.value?.message || nameErrorMessage.value;
        emit(
          "update:errors",
          props.errors.filter((e) => e.field !== "name")
        );
        return nameErrorMessage.value;
      }
      return true;
    };

    const onAddInstance = () => {
      localValue.value.item.configs.push({ ...RULE_CONFIG });
    };

    const onUpdateRuleConfig = (event: RuleConfig, index: number) => {
      localValue.value.item.configs[index] = event;
    };

    const onRemoveInstance = (index: number) => {
      localValue.value.item.configs.splice(index, 1);
      // Remove the validity state for this config
      const newConfigsValidity = { ...configsValidity.value };
      delete newConfigsValidity[index];
      // Reindex the remaining configs
      configsValidity.value = Object.entries(newConfigsValidity).reduce(
        (acc, [key, value]) => {
          const numKey = Number(key);
          if (numKey > index) {
            acc[numKey - 1] = value;
          } else {
            acc[numKey] = value;
          }
          return acc;
        },
        {} as Record<number, boolean>
      );
      updateErrors();
    };

    const onConfigValidityChange = (index: number, valid: boolean) => {
      configsValidity.value = { ...configsValidity.value, [index]: valid };
    };

    const updateOpenPanels = () => {
      const configsLength = localValue.value.item.configs.length;

      if (configsLength > 0) {
        // If a new panel was added, open just that one
        const lastIndex = configsLength - 1;
        if (!openedPanels.value.includes(lastIndex) && lastIndex >= openedPanels.value.length) {
          openedPanels.value.push(lastIndex);
        }
      }
    };

    const validate = async () => {
      const { valid } = (await form.value?.validate()) ?? { valid: false };
      return valid;
    };

    const getErrorsByIndex = (index: number) => {
      return props.errors.find((e: ErrorPayload) => e.field === "configs" && e.index === index)
        ?.errors;
    };

    const isLabelInErrorState = (index: number) => {
      return props.errors.find((e: ErrorPayload) => e.field === "labels" && e.index === index);
    };

    const updateErrors = () => {
      emit(
        "update:errors",
        props.errors.filter((e) => e.field !== "configs" && e.hasOwnProperty("field"))
      );
    };

    const getLabelsChipClass = (color: string, index: number) => {
      return isLabelInErrorState(index) ? "red-faint" : color;
    };

    const getUserLabelsChipClass = (color: string) => {
      return !isUserBasedAuthEnabled.value ? "red-faint" : color;
    };

    const onLabelClick = (index: number) => {
      if (isLabelInErrorState(index)) {
        emit(
          "update:errors",
          props.errors.filter((e) => e.field === "labels" && e.index !== index)
        );
      }
    };

    const updateValid = () => {
      if (props.errors.length === 0) {
        // Cannot check by simple boolean check because v-form initiates values to null,
        // so we need to check against false
        const configValidityValues = Object.values(configsValidity.value);
        const allConfigsValid =
          configValidityValues.length === 0 ||
          configValidityValues.every((valid) => valid !== false);
        const isFormValid = formValid.value !== false;
        emit("update:valid", isFormValid && allConfigsValid);
      }
    };

    onMounted(async () => {
      userLabelsStore.$patch({ loading: true });
      await getLabels();
      await userLabelsStore.getUserLabels();
      await deviceSettingsStore.getDevicesSettings();
    });

    watch(
      () => localValue.value.item.configs.length,
      (newValue, oldValue) => {
        if (oldValue && newValue > oldValue) {
          updateOpenPanels();
        }
      }
    );

    watch(
      () => localValue.value.item.labelType,
      () => {
        localValue.value.item.labels = [];
        localValue.value.item.userLabels = [];
      }
    );

    watch(
      () => localValue,
      () => {
        if (rootError.value) {
          emit(
            "update:errors",
            props.errors.filter((e) => e.hasOwnProperty("field"))
          );
        }
        emit("update:localValue", {
          ...localValue.value.item,
        });
      },
      { deep: true, immediate: true }
    );

    watch(
      [formValid, configsValidity],
      () => {
        updateValid();
      },
      {
        deep: true,
        immediate: true,
      }
    );

    watch(
      () => props.errors,
      () => {
        updateValid();
      },
      {
        deep: true,
        immediate: true,
      }
    );

    return {
      MAX_INSTANCES,
      maxCharactersRule,
      localValue,
      onAddInstance,
      onUpdateRuleConfig,
      required,
      formValid,
      labelRules,
      form,
      labels,
      onRemoveInstance,
      openedPanels,
      validate,
      getErrorsByIndex,
      resourceNameRule,
      previousName,
      updateErrors,
      getLabelsChipClass,
      onLabelClick,
      onConfigValidityChange,
      configsValidity,
      userLabels,
      ZtnaLabelType,
      userLabelRules,
      getUserLabelsChipClass,
      isUserBasedAuthEnabled,
      goToDeviceSettings,
      goToUsersLabels,
      userLabelsLoading,
      isLabelInErrorState,
    };
  },
});
</script>
<style lang="scss" scoped>
.side-by-side-inputs {
  .ports-input {
    flex: 3;
  }
}

.label-popup-block {
  .color-preview {
    width: 12px;
    height: 12px;
    border-radius: 2px;
  }
}

.label-chip.bg-error .v-chip__content span {
  color: rgb(var(--v-theme-white));
}

:deep(*) {
  .v-radio .v-label {
    margin-left: 5px !important;
  }
}
.error-block,
.warning-block {
  min-height: 60px !important;
  padding-top: 5px;
  padding-bottom: 5px;
}
// due to body2 overrides color for body2 and it has bigger specificity than other classes
.coro-link {
  font-size: 16px !important;
}

:deep(*) {
  .v-text-field .v-input__details {
    padding-inline: 0px !important;
  }
  .v-chip__close {
    .icon-close-xs.text-red-dark {
      &::before {
        color: rgb(var(--v-theme-red-dark));
      }
    }
  }
}
</style>
